import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import fr from './fr.json';
import en from './en.json';

// All UI text lives in fr.json / en.json. Missing English keys fall back
// to French, and a missing key shows the key itself so it's easy to spot.
//
// Which language, first match wins:
// 1. saved choice — the FR/EN switch (localStorage + a 1-year cookie), or a
//    signed-in member's saved language (auth.jsx);
// 2. the URL — ?lang=fr|en, /fr/… or /en/…, /secteurs/… (fr) or /sectors/… (en);
// 3. first visit only: the visitor's location (server, /api/lang-hint):
//    Quebec, New Brunswick and French-speaking countries → French, else
//    English; the result is remembered, so it's never re-checked;
// 4. if that fails: the browser's language (fr… → French, else English).
// Bots skip 3 (no location lookup). A small bar says which language was
// chosen automatically, once, with a one-click switch.

export const DICTS = { fr, en };
// Raw value of a key in one language (admin editors prefill both languages).
export const dictValue = (lang, key) => key.split('.').reduce((o, k) => (o == null ? o : o[k]), DICTS[lang]);
const STORAGE_KEY = 'pme_lang';      // explicit choice
const AUTO_KEY = 'pme_lang_auto';    // chosen automatically on the first visit
const COOKIE_YEAR = 365 * 24 * 3600;

const lookup = (dict, key) => key.split('.').reduce((o, k) => (o == null ? o : o[k]), dict);
const valid = (l) => (l === 'fr' || l === 'en' ? l : null);
const read = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };
const cookieLang = () => { try { return valid(document.cookie.match(/(?:^|;\s*)pme_lang=(fr|en)/)?.[1]); } catch { return null; } };
const BOT = /bot\b|crawl|spider|slurp|bingpreview|facebookexternalhit|embedly|whatsapp|telegrambot|discordbot|linkedinbot|pinterest|yandex|baiduspider|duckduckbot|applebot|lighthouse/i;
const isBot = () => { try { return BOT.test(navigator.userAgent || ''); } catch { return false; } };
const browserLang = () => { try { return String((navigator.languages && navigator.languages[0]) || navigator.language || '').toLowerCase().startsWith('fr') ? 'fr' : 'en'; } catch { return 'fr'; } };

// Language carried by a URL (rule 2), or null.
export function urlLang(loc = window.location) {
  const q = valid(new URLSearchParams(loc.search).get('lang'));
  if (q) return q;
  const m = loc.pathname.match(/^\/(fr|en)(\/|$)/);
  if (m) return m[1];
  if (/^\/(secteurs|equipe)(\/|$)/.test(loc.pathname)) return 'fr';
  if (/^\/(sectors|team)(\/|$)/.test(loc.pathname)) return 'en';
  return null;
}

export const savedLang = () => valid(read(STORAGE_KEY)) || cookieLang();

function initialState() {
  const saved = savedLang();
  if (saved) return { lang: saved, source: 'saved', bar: false };
  let url = null;
  try { url = urlLang(); } catch { /* no window */ }
  if (url) { write(AUTO_KEY, url); return { lang: url, source: 'url', bar: false }; }
  const auto = valid(read(AUTO_KEY));
  if (auto) return { lang: auto, source: 'auto', bar: false };
  // First visit: the browser's language while the location is checked.
  return { lang: browserLang(), source: isBot() ? 'bot' : 'pending', bar: false };
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [state, setState] = useState(initialState);
  const { lang, source } = state;

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Rule 3/4, once: ask the server where the visitor is.
  useEffect(() => {
    if (source !== 'pending') return undefined;
    let alive = true;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    fetch('/api/lang-hint', { cache: 'no-store', signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((d) => {
        clearTimeout(timer);
        if (!alive || savedLang()) return; // picked a language meanwhile
        const next = valid(d?.lang) || browserLang();
        write(AUTO_KEY, next);
        setState({ lang: next, source: 'auto', bar: true });
      });
    return () => { alive = false; clearTimeout(timer); ctrl.abort(); };
  }, [source]);

  // Explicit choice: kept in this browser for a year (localStorage + cookie).
  const setLang = useCallback((next) => {
    if (!valid(next)) return;
    setState({ lang: next, source: 'saved', bar: false });
    write(STORAGE_KEY, next);
    try { document.cookie = `pme_lang=${next}; max-age=${COOKIE_YEAR}; path=/; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`; } catch { /* ignore */ }
  }, []);
  // A language from the URL, unless one was picked (rule 2 after rule 1).
  const applyUrlLang = useCallback((next) => {
    if (!valid(next) || savedLang()) return;
    write(AUTO_KEY, next);
    setState((s) => (s.lang === next ? s : { lang: next, source: 'url', bar: false }));
  }, []);
  const dismissBar = useCallback(() => setState((s) => ({ ...s, bar: false })), []);

  const t = useCallback((key, vars) => {
    let s = lookup(DICTS[lang], key);
    if (typeof s !== 'string') s = lookup(DICTS.fr, key);
    if (typeof s !== 'string') return key;
    return vars ? s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m)) : s;
  }, [lang]);

  // Raw value (array/object) for structured content such as page sections;
  // falls back to French like t().
  const tm = useCallback((key) => {
    const v = lookup(DICTS[lang], key);
    return v === undefined ? lookup(DICTS.fr, key) : v;
  }, [lang]);

  // Turns an ApiError into a translated message (errors.<code>).
  const errorText = useCallback((err) => {
    const key = `errors.${err?.code || 'server_error'}`;
    const s = t(key);
    const base = s === key ? t('errors.server_error') : s;
    if (err?.detail && err.code === 'smtp_failed') return `${base}: ${err.detail}`;
    if (err?.detail && (err.code === 'server_error' || s === key)) return `${base} (${err.detail})`;
    return base;
  }, [t]);

  const value = useMemo(() => ({ lang, setLang, t, tm, errorText, langSource: source, langBar: state.bar, dismissBar, applyUrlLang }), [lang, setLang, t, tm, errorText, source, state.bar, dismissBar, applyUrlLang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);

// Sets the browser tab title for a page, in the current language.
export function usePageTitle(key) {
  const { t } = useI18n();
  const title = key ? t(key) : null;
  useEffect(() => {
    document.title = title ? `${title} | ${t('brand.name')}` : t('brand.name');
  }, [title, t]);
}
